<script>
import { mapGetters } from 'vuex'
import AppBus from '@C/bus/app-bus.vue'
import { v4 as uuidv4 } from 'uuid'
import { triggerEvent, needsExtensionUpdate } from '@/js/utils.js'

export default {
  name: 'extension-mixin',
  computed: {
    ...mapGetters([
      'hasExtension',
      'extensionVersion',
      'extensionVersionPromise',
      'extensionNetworkRequestAction'
    ])
  },
  methods: {
    async initializeExtensionNetworkRequestAction () {
      if (this.extensionNetworkRequestAction) {
        return this.extensionNetworkRequestAction
      }
      const networkRequestAction = await this.postMessage({
        action: 'gnra'
      }, true)
      this.$store.commit('extensionNetworkRequestAction', networkRequestAction)
      return networkRequestAction
    },
    async isExtensionV3OrHigher () {
      await this.extensionVersionPromise
      const isV3 = !needsExtensionUpdate('3.0.0', this.extensionVersion)
      return isV3
    },
    genericRequest (requestEvent, responseEvent, data) {
      const __evtId = `${requestEvent}-${uuidv4()}`
      return new Promise((resolve, reject) => {
        AppBus.$on(responseEvent, function once (data) {
          if (data.__evtId === __evtId) {
            AppBus.$off(responseEvent, once)
            resolve(data)
          }
        })

        triggerEvent(document, requestEvent, {
          __evtId,
          ...data
        })
      })
    },
    async newGenericRequest (requestEvent, data) {
      if (await this.isExtensionV3OrHigher()) {
        return this.postMessage({
          action: requestEvent,
          data
        }, true)
      }
      const __responseEvent = `${requestEvent}-${uuidv4()}`
      return new Promise((resolve, reject) => {
        AppBus.$on(__responseEvent, function once (data) {
          AppBus.$off(__responseEvent, once)
          resolve(data)
        })
        triggerEvent(document, requestEvent, {
          __responseEvent,
          ...data
        })
      })
    },
    async postMessage (data, expectResponse = false) {
      const ackID = `${data.action}-${uuidv4()}`
      data.__responseEvent = ackID
      let promise
      if (expectResponse) {
        promise = new Promise(resolve => {
          window.addEventListener('message', function once (msg) {
            try {
              const { data = {} } = msg
              const { action } = data
              if (action !== ackID) {
                return
              }
              window.removeEventListener('message', once)
              resolve(data.data)
            } catch (e) {
            }
          })
        })
      } else {
        promise = Promise.resolve()
      }
      window.postMessage({
        ...data,
        __responseEvent: ackID
      })
      return promise
    },
    async sameSiteBypass (domains) {
      return this.postMessage({
        action: 'ext:sameSiteBypass',
        data: domains
      }, true)
    },
    async getExtData (query) {
      console.log('Making getExtData request')
      const isv3 = await this.isExtensionV3OrHigher()
      console.log(`isV3=${isv3}`)
      if (isv3) {
        console.log(`Making ext:getData request`)
        const result = await this.newGenericRequest('ext:getData', query)
        const { credentials = {}, mediaEntry } = result
        const { authResult = {}, profile } = credentials
        const { access_token: accessToken, refresh_token: refreshToken } = authResult
        return {
          ...mediaEntry,
          credentials: {
            access_token: accessToken,
            refresh_token: refreshToken,
            profile
          }
        }
      } else {
        // TODO: Remvoe this once v3 is stable
        return this.genericRequest('get-ext-data', 'on-ext-data', query)
      }
    },
    afterTabMediaFetch (data) {
      triggerEvent(document, 'after-tab-media-fetch', data)
    },
    // XXX: Remove this after extension v2.1.2.5
    updateOldModuleToCurrent (module, data) {
      let ret = {}
      if (typeof module === 'string') {
        Object.assign(ret, {
          name: module,
          loadMediaFromModule: data.videoData.loadMediaFromModule || true,
          callModuleBG: data.videoData.callModuleBg,
          addHeaders: false,
          useLocalURL: true
        })
      } else {
        ret = module
      }
      return ret
    },
    async requestModuleHelp (module, data) {
      if (await this.isExtensionV3OrHigher()) {
        const result = await this.postMessage({
          action: 'ext:module:help',
          data
        }, true)
        return result
      }
      module = Object.assign({}, this.updateOldModuleToCurrent(module, data))
      return this.genericRequest(`request-module-help`, `on-module-help`, {
        module,
        ...JSON.parse(JSON.stringify(data))
      })
    },
    async loadMediaFromModule (module, data) {
      if (await this.isExtensionV3OrHigher()) {
        const result = await this.postMessage({
          action: 'ext:load-media-from-module',
          data
        }, true)
        return result
      }
      module = Object.assign({}, this.updateOldModuleToCurrent(module, data))
      return this.genericRequest('load-media-from-module', 'load-media-from-module-complete', {
        module,
        ...JSON.parse(JSON.stringify(data))
      })
    },
    async moduleAfterMediaLoad (module, data) {
      module = Object.assign({}, this.updateOldModuleToCurrent(module, data))
      return this.genericRequest('module-after-media-load', 'module-after-media-load-complete', {
        module,
        ...JSON.parse(JSON.stringify(data))
      })
    },
    buildMediaEntryFromExtensionData (data) {
      const result = {
        videoSelector: data.videoSelector,
        videoURL: data.videoURL,
        videoData: data.videoData,
        position: data.position
      }
      // The following are part of data which will be lost while creating MediaEntry
      // So, we copy it over to videoData
      Object.assign(result.videoData, {
        origin: data.origin,
        strategies: data.strategies,
        headers: data.headers
      })
      return result
    },
    async searchForMedia (data) {
      return this.genericRequest('search-for-media', 'search-for-media-complete', {
        ...JSON.parse(JSON.stringify(data))
      })
    },
    requestXHRHelp (url, options = {}) {
      return this.genericRequest('xhr-help', 'xhr-help-complete', {
        url,
        ...options
      })
    },
    async sendMessageToBG (event, args, expectResponse = true) {
      return this.postMessage({
        action: 'ts:bridge',
        data: {
          event,
          args
        }
      }, expectResponse)
    }
  }
}
</script>
